/**
 * Photos and videos on client notes and appointment comments (boona: docs/codebase/attachments/README.md).
 * A file belongs to exactly one note or one appointment; staff only.
 */
export type AttachmentKind = "photo" | "video"

export interface AttachmentItem {
  id: string
  kind: AttachmentKind
  /** The file (a short-lived signed link from the API) */
  src: string
  /** A small preview: the photo's thumbnail or the video's poster frame */
  thumb: string
  /** Who added it and when, ready to show */
  by: string
  when: string
  size?: string
  /** "0:42" for a video */
  duration?: string
  /** A video still being converted on the server */
  processing?: boolean
  /** The conversion failed: the file can only be deleted (and sent again) */
  failed?: boolean
  status?: "uploading" | "processing" | "ready" | "failed"
  /** The link that downloads instead of showing */
  downloadSrc?: string
  /** Whether this viewer may delete it (the server's rights) */
  canDelete?: boolean
}

/** A file on its way up: what the tiles under a comment or note show until it is saved */
export interface PendingUpload {
  id: string
  name: string
  kind: AttachmentKind | null
  /** A local object URL for the preview, while uploading */
  previewUrl: string
  /** Whether the browser can draw it (HEIC from a Mac cannot) */
  displayable: boolean
  /** 0..1 */
  progress: number
  state: "uploading" | "processing" | "ready" | "error"
  error?: string
  duration?: string
  sizeBytes: number
  /** The server's attachment id once it exists (a note's photo waits for the note with it) */
  serverId?: number
}

/** The limits the server enforces too (README §5) */
export const ATTACHMENT_LIMITS = {
  photoBytes: 20 * 1024 * 1024,
  videoBytes: 250 * 1024 * 1024,
  videoSeconds: 120,
  perItem: 10,
} as const
